import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Upload, Download, CheckCircle, XCircle, AlertTriangle, Image, FileSpreadsheet, RefreshCw, SkipForward, ChevronDown, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const MotorcycleBulkUpload = ({ 
  downloadTemplate, 
  onSuccess,
  getAuthHeader,
  API,
  showFixMissingCards = false,
  BulkRegistrationCardComponent = null
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fixCardsDialogOpen, setFixCardsDialogOpen] = useState(false);
  const [foodPermitDialogOpen, setFoodPermitDialogOpen] = useState(false);
  const [foodPermitFiles, setFoodPermitFiles] = useState([]);
  const [foodPermitResult, setFoodPermitResult] = useState(null);
  const [foodPermitLoading, setFoodPermitLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [duplicateRecords, setDuplicateRecords] = useState([]);
  const [hasDuplicates, setHasDuplicates] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [imageStats, setImageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(null); // 'update' or 'skip'

  const handleExcelChange = (e) => {
    setExcelFile(e.target.files[0]);
    setUploadResult(null);
    setPreviewData(null);
    setDuplicateRecords([]);
    setHasDuplicates(false);
    setImageStats(null);
    setDuplicateMode(null);
  };

  const handleZipChange = (e) => {
    setZipFile(e.target.files[0]);
    setUploadResult(null);
    setPreviewData(null);
    setDuplicateRecords([]);
    setHasDuplicates(false);
    setImageStats(null);
    setDuplicateMode(null);
  };

  const handleUpload = async () => {
    if (!excelFile) {
      toast.error('Please select an Excel file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      if (zipFile) {
        formData.append('images_zip', zipFile);
      }

      const response = await fetch(`${API}/motorcycles/bulk-upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        setPreviewData(result.preview_data || []);
        setDuplicateRecords(result.duplicate_records || []);
        setHasDuplicates(result.has_duplicates || false);
        setBatchId(result.batch_id);
        setImageStats({
          withImages: result.with_registration_cards,
          withoutImages: result.without_registration_cards,
          imagesInZip: result.images_found_in_zip,
          unmatchedImages: result.unmatched_images || [],
          unmatchedMotorcycles: result.unmatched_motorcycles || []
        });
        
        if (result.has_duplicates) {
          toast.info(`${result.duplicate_count} duplicate records found. Please choose how to handle them.`);
        } else {
          toast.success(`${result.total_count} motorcycles ready for upload`);
        }
      } else {
        setUploadResult(result);
        toast.error('Validation errors found. Please review and correct.');
      }
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateModeSelect = (mode) => {
    setDuplicateMode(mode);
  };

  const handleConfirm = async () => {
    // If there are duplicates and no mode selected, show warning
    if (hasDuplicates && duplicateRecords.length > 0 && !duplicateMode) {
      toast.error('Please select how to handle duplicate records');
      return;
    }

    setLoading(true);
    try {
      // Combine new records and duplicates based on mode
      const allMotorcycles = [...previewData];
      if (duplicateMode === 'update') {
        allMotorcycles.push(...duplicateRecords);
      }

      const response = await fetch(`${API}/motorcycles/bulk-confirm`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          motorcycles: allMotorcycles,
          mode: duplicateMode || 'skip',
          batch_id: batchId
        })
      });

      const result = await response.json();
      
      if (result.success) {
        let message = '';
        if (result.created_count > 0) {
          message += `Created ${result.created_count} new motorcycles`;
        }
        if (result.updated_count > 0) {
          message += message ? `, updated ${result.updated_count} existing` : `Updated ${result.updated_count} motorcycles`;
        }
        if (result.skipped_count > 0) {
          message += message ? `, skipped ${result.skipped_count} duplicates` : `Skipped ${result.skipped_count} duplicates`;
        }
        if (result.images_saved > 0) {
          message += ` with ${result.images_saved} registration card images`;
        }
        toast.success(message || 'Upload completed');
        setDialogOpen(false);
        resetDialog();
        if (onSuccess) onSuccess();
      } else {
        toast.error(result.detail || 'Failed to save motorcycles');
      }
    } catch (error) {
      toast.error('Failed to confirm upload');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setExcelFile(null);
    setZipFile(null);
    setUploadResult(null);
    setPreviewData(null);
    setDuplicateRecords([]);
    setHasDuplicates(false);
    setBatchId(null);
    setImageStats(null);
    setDuplicateMode(null);
  };

  // Filter columns for display (hide large image data)
  const getDisplayColumns = (data) => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter(key => 
      key !== 'registration_card_image' && 
      key !== 'has_registration_card' &&
      key !== 'lessee_id' &&
      key !== 'assigned_rider_id' &&
      key !== 'existing_id' &&
      key !== 'is_duplicate' &&
      key !== 'image_batch_id'
    );
  };

  const totalRecords = (previewData?.length || 0) + (duplicateRecords?.length || 0);

  return (
    <>
      <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <PopoverTrigger asChild>
          <Button
            className="bg-gradient-to-r from-lime-600 to-green-600 text-xs px-2 py-1.5 h-8 whitespace-nowrap"
            data-testid="bulk-actions-btn"
          >
            <Upload className="w-3 h-3 mr-1" />
            Bulk Upload
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-1" align="end">
          <Button
            variant="ghost"
            className="w-full justify-start text-xs h-8"
            onClick={() => { downloadTemplate(); setDropdownOpen(false); }}
            data-testid="download-motorcycle-template-btn"
          >
            <Download className="w-3 h-3 mr-2" />
            Download Template
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-xs h-8"
            onClick={() => { setDialogOpen(true); setDropdownOpen(false); }}
            data-testid="bulk-upload-motorcycle-btn"
          >
            <FileSpreadsheet className="w-3 h-3 mr-2" />
            Upload Motorcycles
          </Button>
          {showFixMissingCards && BulkRegistrationCardComponent && (
            <Button
              variant="ghost"
              className="w-full justify-start text-xs h-8"
              onClick={() => { setFixCardsDialogOpen(true); setDropdownOpen(false); }}
              data-testid="fix-missing-cards-btn"
            >
              <Wrench className="w-3 h-3 mr-2" />
              Fix Missing Cards
            </Button>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-xs h-8"
            onClick={() => { setFoodPermitDialogOpen(true); setDropdownOpen(false); }}
            data-testid="bulk-upload-food-permits-btn"
          >
            <FileSpreadsheet className="w-3 h-3 mr-2" />
            Bulk Upload Food Permits
          </Button>
        </PopoverContent>
      </Popover>

      {/* Bulk Upload Food Permits Dialog — match files to bikes by
          the LAST 4 DIGITS of the plate number in the filename. */}
      <Dialog
        open={foodPermitDialogOpen}
        onOpenChange={(o) => {
          setFoodPermitDialogOpen(o);
          if (!o) { setFoodPermitFiles([]); setFoodPermitResult(null); }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Food Permits</DialogTitle>
            <DialogDescription>
              Select multiple files (PDF / image). Each file must contain
              the plate&apos;s <strong>last 4 digits</strong> in its filename
              (e.g. <code>19851.pdf</code> or <code>DXB-2-19851.pdf</code>).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => { setFoodPermitFiles(Array.from(e.target.files || [])); setFoodPermitResult(null); }}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
              data-testid="food-permit-files-input"
            />
            {foodPermitFiles.length > 0 && (
              <p className="text-sm text-slate-600">{foodPermitFiles.length} file(s) selected</p>
            )}
            {foodPermitResult && (
              <div className="rounded-lg border p-3 text-sm bg-slate-50 space-y-1">
                <p><strong>Uploaded:</strong> {foodPermitResult.uploaded} / {foodPermitResult.total_files}</p>
                {foodPermitResult.no_match > 0 && <p className="text-amber-700"><strong>No match:</strong> {foodPermitResult.no_match}</p>}
                {foodPermitResult.ambiguous > 0 && <p className="text-orange-700"><strong>Ambiguous plate collisions:</strong> {foodPermitResult.ambiguous}</p>}
                {foodPermitResult.failed > 0 && <p className="text-red-700"><strong>Failed:</strong> {foodPermitResult.failed}</p>}
                <div className="max-h-40 overflow-y-auto mt-2 text-xs font-mono">
                  {(foodPermitResult.results || []).map((r, i) => (
                    <div key={i} className={
                      r.status === 'uploaded' ? 'text-green-700' :
                      r.status === 'ambiguous' ? 'text-orange-700' :
                      r.status === 'no_match' ? 'text-amber-700' : 'text-red-700'
                    }>
                      {r.status.toUpperCase()} · {r.filename}{r.plate_number ? ` → ${r.plate_number}` : ''}{r.reason ? ` (${r.reason})` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setFoodPermitDialogOpen(false)}
                disabled={foodPermitLoading}
              >
                Close
              </Button>
              <Button
                disabled={!foodPermitFiles.length || foodPermitLoading}
                onClick={async () => {
                  setFoodPermitLoading(true);
                  try {
                    const fd = new FormData();
                    foodPermitFiles.forEach(f => fd.append('files', f));
                    const resp = await fetch(`${API}/motorcycles/bulk-upload-food-permits`, {
                      method: 'POST',
                      headers: getAuthHeader(),
                      body: fd,
                    });
                    const j = await resp.json();
                    if (!resp.ok) {
                      toast.error(j.detail || 'Upload failed');
                    } else {
                      setFoodPermitResult(j);
                      toast.success(`${j.uploaded} of ${j.total_files} food permits uploaded`);
                      if (j.uploaded > 0 && onSuccess) onSuccess();
                    }
                  } catch (e) {
                    toast.error('Upload failed');
                  } finally {
                    setFoodPermitLoading(false);
                  }
                }}
                className="bg-gradient-to-r from-amber-600 to-orange-600"
                data-testid="upload-food-permits-btn"
              >
                {foodPermitLoading ? 'Uploading...' : `Upload ${foodPermitFiles.length} File(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fix Missing Cards Dialog */}
      {showFixMissingCards && BulkRegistrationCardComponent && (
        <BulkRegistrationCardComponent
          onSuccess={onSuccess}
          getAuthHeader={getAuthHeader}
          API={API}
          externalOpen={fixCardsDialogOpen}
          onExternalOpenChange={setFixCardsDialogOpen}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Motorcycles</DialogTitle>
            <DialogDescription>
              Upload an Excel file with motorcycle data. Optionally, upload a ZIP file containing registration card images (JPEG) named by chassis number.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            {!previewData && !hasDuplicates && (
              <div className="space-y-4">
                {/* Excel File */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    <span className="font-medium">Excel File (Required)</span>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelChange}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    data-testid="bulk-upload-excel-input"
                  />
                  {excelFile && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>{excelFile.name}</span>
                    </div>
                  )}
                </div>

                {/* ZIP File for Images */}
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Registration Card Images (Optional)</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-2">
                    Upload a ZIP file containing JPEG images. Each image should be named with the chassis number (e.g., R3RSR3024PA000682.jpg)
                  </p>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={handleZipChange}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="bulk-upload-zip-input"
                  />
                  {zipFile && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span>{zipFile.name}</span>
                    </div>
                  )}
                </div>

                {/* Validation Errors */}
                {uploadResult && !uploadResult.success && (
                  <div className="space-y-3">
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <Alert className="border-red-500 bg-red-50">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <AlertDescription>
                          <p className="font-semibold text-red-900 mb-2">Validation Errors:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm max-h-40 overflow-y-auto">
                            {uploadResult.errors.map((error, idx) => (
                              <li key={idx} className="text-red-800">{error}</li>
                            ))}
                          </ul>
                          <p className="mt-2 text-sm text-red-700">Please correct these errors and upload again.</p>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="text-sm text-slate-600">
                      <p>Valid records: {uploadResult.valid_count} / {uploadResult.total_count}</p>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleUpload}
                  disabled={!excelFile || loading}
                  className="w-full bg-gradient-to-r from-lime-600 to-green-600"
                  data-testid="validate-preview-btn"
                >
                  {loading ? 'Validating...' : 'Validate & Preview'}
                </Button>
              </div>
            )}

            {/* Duplicate Records Handling Dialog */}
            {hasDuplicates && duplicateRecords.length > 0 && !duplicateMode && (
              <div className="space-y-4" data-testid="duplicate-handling-section">
                <Alert className="border-orange-500 bg-orange-50">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <AlertDescription>
                    <p className="font-semibold text-orange-900 mb-2">
                      Duplicate Records Found ({duplicateRecords.length})
                    </p>
                    <p className="text-sm text-orange-700 mb-3">
                      The following plate numbers already exist in the system. How would you like to handle them?
                    </p>
                    <div className="max-h-32 overflow-y-auto bg-white p-2 rounded border text-xs font-mono mb-3">
                      {duplicateRecords.map((record, idx) => (
                        <span key={idx} className="inline-block mr-2 mb-1 px-2 py-1 bg-orange-100 rounded">
                          {record.plate_number}
                        </span>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 border-2 hover:border-blue-500 hover:bg-blue-50"
                    onClick={() => handleDuplicateModeSelect('update')}
                    data-testid="update-duplicates-btn"
                  >
                    <RefreshCw className="w-8 h-8 text-blue-600" />
                    <span className="font-semibold text-blue-900">Update Existing</span>
                    <span className="text-xs text-slate-500 text-center">
                      Replace existing data with new values from Excel
                    </span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 border-2 hover:border-amber-500 hover:bg-amber-50"
                    onClick={() => handleDuplicateModeSelect('skip')}
                    data-testid="skip-duplicates-btn"
                  >
                    <SkipForward className="w-8 h-8 text-amber-600" />
                    <span className="font-semibold text-amber-900">Skip Duplicates</span>
                    <span className="text-xs text-slate-500 text-center">
                      Keep existing records, only add new ones
                    </span>
                  </Button>
                </div>

                {previewData && previewData.length > 0 && (
                  <p className="text-sm text-slate-600 text-center">
                    {previewData.length} new motorcycles will also be added
                  </p>
                )}
              </div>
            )}

            {/* Preview Data (shown after duplicate mode selection or if no duplicates) */}
            {(previewData && (duplicateMode || !hasDuplicates)) && (
              <div className="space-y-4" data-testid="preview-section">
                {/* Success/Summary Alert */}
                <Alert className="border-green-500 bg-green-50">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <AlertDescription>
                    <p className="font-semibold text-green-900">Ready to Upload!</p>
                    <div className="text-sm text-green-700 mt-1 space-y-1">
                      {previewData.length > 0 && (
                        <p><strong>{previewData.length}</strong> new motorcycles will be created</p>
                      )}
                      {duplicateMode === 'update' && duplicateRecords.length > 0 && (
                        <p><strong>{duplicateRecords.length}</strong> existing motorcycles will be updated</p>
                      )}
                      {duplicateMode === 'skip' && duplicateRecords.length > 0 && (
                        <p><strong>{duplicateRecords.length}</strong> duplicates will be skipped</p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>

                {/* Mode change option */}
                {hasDuplicates && duplicateMode && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {duplicateMode === 'update' ? (
                        <>
                          <RefreshCw className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-blue-800">
                            Duplicates will be <strong>updated</strong>
                          </span>
                        </>
                      ) : (
                        <>
                          <SkipForward className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-amber-800">
                            Duplicates will be <strong>skipped</strong>
                          </span>
                        </>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDuplicateMode(null)}
                      className="text-xs"
                    >
                      Change
                    </Button>
                  </div>
                )}

                {/* Image Statistics */}
                {imageStats && (
                  <div className="space-y-3">
                    <div className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4 text-blue-600" />
                        <span className="text-sm">
                          <strong>{imageStats.withImages}</strong> with registration cards
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          <strong>{imageStats.withoutImages}</strong> without images
                        </span>
                      </div>
                      {imageStats.imagesInZip > 0 && (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-700">
                            {imageStats.imagesInZip} images found in ZIP
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Show mismatches if any */}
                    {imageStats.withoutImages > 0 && imageStats.unmatchedMotorcycles?.length > 0 && (
                      <Alert className="border-orange-500 bg-orange-50">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <AlertDescription>
                          <p className="font-semibold text-orange-900 mb-2">
                            Image Mismatch Detected ({imageStats.withoutImages} motorcycles without images)
                          </p>
                          <p className="text-xs text-orange-700 mb-2">
                            The following chassis numbers have no matching image file in the ZIP:
                          </p>
                          <div className="max-h-24 overflow-y-auto bg-white p-2 rounded border text-xs font-mono">
                            {imageStats.unmatchedMotorcycles.map((chassis, idx) => (
                              <span key={idx} className="inline-block mr-2 mb-1 px-1 bg-orange-100 rounded">{chassis}</span>
                            ))}
                            {imageStats.unmatchedMotorcycles.length >= 20 && <span className="text-orange-600">... and more</span>}
                          </div>
                          <p className="mt-2 text-xs text-orange-700">
                            <strong>Fix:</strong> Ensure image filenames match chassis numbers exactly (e.g., ME4JF642NPT100011.jpg)
                          </p>
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {imageStats.unmatchedImages?.length > 0 && (
                      <Alert className="border-blue-500 bg-blue-50">
                        <AlertTriangle className="w-4 h-4 text-blue-600" />
                        <AlertDescription>
                          <p className="font-semibold text-blue-900 mb-2">
                            Unused Images in ZIP ({imageStats.unmatchedImages.length} images)
                          </p>
                          <p className="text-xs text-blue-700 mb-2">
                            These image filenames don&apos;t match any chassis number in Excel:
                          </p>
                          <div className="max-h-24 overflow-y-auto bg-white p-2 rounded border text-xs font-mono">
                            {imageStats.unmatchedImages.map((img, idx) => (
                              <span key={idx} className="inline-block mr-2 mb-1 px-1 bg-blue-100 rounded">{img}</span>
                            ))}
                            {imageStats.unmatchedImages.length >= 20 && <span className="text-blue-600">... and more</span>}
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Data Preview Table */}
                <div className="max-h-80 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs whitespace-nowrap sticky top-0 bg-white">TYPE</TableHead>
                        <TableHead className="text-xs whitespace-nowrap sticky top-0 bg-white">IMAGE</TableHead>
                        {getDisplayColumns([...previewData, ...duplicateRecords]).slice(0, 7).map((key) => (
                          <TableHead key={key} className="text-xs whitespace-nowrap sticky top-0 bg-white">
                            {key.replace(/_/g, ' ').toUpperCase()}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* New records */}
                      {previewData.map((row, idx) => (
                        <TableRow key={`new-${idx}`}>
                          <TableCell className="text-xs">
                            <Badge className="bg-green-100 text-green-800">New</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.has_registration_card ? (
                              <Badge className="bg-green-100 text-green-800">
                                <Image className="w-3 h-3 mr-1" />
                                Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-400">
                                No
                              </Badge>
                            )}
                          </TableCell>
                          {getDisplayColumns([...previewData, ...duplicateRecords]).slice(0, 7).map((key) => (
                            <TableCell key={key} className="text-xs whitespace-nowrap">
                              {row[key]?.toString() || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                      {/* Duplicate records (if mode is update) */}
                      {duplicateMode === 'update' && duplicateRecords.map((row, idx) => (
                        <TableRow key={`dup-${idx}`} className="bg-blue-50">
                          <TableCell className="text-xs">
                            <Badge className="bg-blue-100 text-blue-800">Update</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.has_registration_card ? (
                              <Badge className="bg-green-100 text-green-800">
                                <Image className="w-3 h-3 mr-1" />
                                Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-400">
                                No
                              </Badge>
                            )}
                          </TableCell>
                          {getDisplayColumns([...previewData, ...duplicateRecords]).slice(0, 7).map((key) => (
                            <TableCell key={key} className="text-xs whitespace-nowrap">
                              {row[key]?.toString() || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={resetDialog}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="bg-gradient-to-r from-lime-600 to-green-600"
                    data-testid="confirm-upload-btn"
                  >
                    {loading ? 'Uploading...' : `Confirm & Upload ${totalRecords} Motorcycles`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MotorcycleBulkUpload;
